import React, { useState } from 'react';
import { Upload, Save, Check, Loader2, FolderOpen } from 'lucide-react';
import Papa, { ParseResult } from 'papaparse';
import { SavedDatasetsBrowser } from '../SavedDatasetsBrowser';
import { storageService } from '../../services/storageService';
import { useLogto } from '@logto/react';

// File size limits
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface DataUploadSectionProps {
  onDataLoaded: (dataFileId: string, dataColumns: string[]) => void;
  onError?: (error: string) => void;
}

export const DataUploadSection: React.FC<DataUploadSectionProps> = ({
  onDataLoaded,
  onError,
}) => {
  const [showSavedDatasets, setShowSavedDatasets] = useState(false);
  const [savingDataset, setSavingDataset] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [fileInfo, setFileInfo] = useState<{
    name: string;
    rows: number;
    columns: number;
    columnNames: string[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const logto = useLogto();


  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const parseCSV = async (file: File) => {
    setIsLoading(true);
    setErrorMessage(null);

    // Validate file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const error = `File too large (${sizeMB}MB). Maximum allowed size is ${MAX_FILE_SIZE_MB}MB.`;
      setErrorMessage(error);
      if (onError) onError(error);
      setIsLoading(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const content = e.target?.result as string;
      Papa.parse(content, {
        header: false,
        skipEmptyLines: true,
        complete: async (results: ParseResult<string[]>) => {
          try {
            const data = results.data as string[][];

            if (data.length === 0) {
              const error = 'CSV file is empty';
              setErrorMessage(error);
              if (onError) onError(error);
              setIsLoading(false);
              return;
            }

            const columnNames = data[0];
            const numRows = data.length - 1;
            const numColumns = columnNames.length;

            if (columnNames.some((col) => !col || col.trim() === '')) {
              const error = 'CSV has empty column names';
              setErrorMessage(error);
              if (onError) onError(error);
              setIsLoading(false);
              return;
            }

            const info = { name: file.name, rows: numRows, columns: numColumns, columnNames };
            setFileInfo(info);

            try {
              const saveResult = await storageService.saveDataset(
                logto,
                file,
                file.name.replace('.csv', '') || 'dataset',
                `Uploaded on ${new Date().toLocaleString()}`
              );

              if (saveResult.success && saveResult.dataset_id) {
                onDataLoaded(saveResult.dataset_id, columnNames);
              } else {
                throw new Error(saveResult.message || 'Failed to save dataset');
              }
            } catch (saveError) {
              const errorMsg = saveError instanceof Error ? saveError.message : 'Failed to save dataset';
              setErrorMessage(`Dataset loaded but failed to save: ${errorMsg}`);
              if (onError) onError(errorMsg);
            }

            setIsLoading(false);
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Failed to parse CSV';
            setErrorMessage(errorMsg);
            if (onError) onError(errorMsg);
            setIsLoading(false);
          }
        },
        error: (error: Error) => {
          const errorMsg = error?.message || 'Failed to parse CSV file';
          setErrorMessage(errorMsg);
          if (onError) onError(errorMsg);
          setIsLoading(false);
        },
      });
    };
    reader.onerror = () => {
      const error = 'Failed to read file. Please try again.';
      setErrorMessage(error);
      if (onError) onError(error);
      setIsLoading(false);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        parseCSV(file);
      } else {
        setErrorMessage('Please upload a CSV file');
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      parseCSV(files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFileInfo(null);
    setErrorMessage(null);
  };

  const handleSaveDataset = async () => {
    if (!fileInfo) return;
    const datasetName = window.prompt('Enter a name for this dataset:', fileInfo.name.replace('.csv', ''));
    if (!datasetName) return;

    setSavingDataset(true);
    try {
      const fileInput = document.querySelector('input[type="file"][accept=".csv"]') as HTMLInputElement;
      if (fileInput?.files?.[0]) {
        const result = await storageService.saveDataset(
          logto,
          fileInput.files[0],
          datasetName,
          undefined
        );
        if (result.success) {
          alert(`Dataset "${datasetName}" saved!`);
        } else {
          alert(`Failed: ${result.message}`);
        }
      }
    } catch (error) {
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSavingDataset(false);
    }
  };

  const handleLoadSavedDataset = async (datasetId: string, datasetName: string) => {
    setShowSavedDatasets(false);
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const result = await storageService.getDataset(logto, datasetId);

      if (result.success && result.metadata) {
        const columns = result.metadata.columns || [];

        if (columns.length === 0) {
          throw new Error('Dataset has no columns');
        }

        // Update local file info display
        setFileInfo({
          name: datasetName,
          rows: result.metadata.rows || 0,
          columns: columns.length,
          columnNames: columns,
        });

        // Proceed to next step
        onDataLoaded(datasetId, columns);
      } else {
        throw new Error(result.message || 'Failed to load dataset');
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to load dataset';
      setErrorMessage(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Upload Data</h2>
        <button onClick={() => setShowSavedDatasets(true)} className="btn btn-secondary text-sm">
          <FolderOpen className="w-4 h-4" /> Load Saved
        </button>
      </div>

      {showSavedDatasets && (
        <SavedDatasetsBrowser
          onSelectDataset={handleLoadSavedDataset}
          onClose={() => setShowSavedDatasets(false)}
        />
      )}

      {!fileInfo ? (
        <>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition ${isDragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 bg-gray-50'
              } cursor-pointer hover:border-blue-400`}
          >
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-700 font-medium mb-1">Drop your CSV file here</p>
            <p className="text-gray-500 text-sm mb-4">or click to browse</p>
            <label className="inline-block">
              <input type="file" accept=".csv" onChange={handleFileInputChange} className="hidden" disabled={isLoading} />
              <span className="btn btn-primary">{isLoading ? 'Processing...' : 'Select File'}</span>
            </label>
          </div>

          <div className="info-box">
            <h3 className="info-title">Data Requirements</h3>
            <ul>
              <li>First row must be column headers</li>
              <li>Include a date column (YYYY-MM-DD format)</li>
              <li>Include your target variable (revenue, sales, etc.)</li>
              <li>Include media spend columns for each channel</li>
            </ul>
          </div>
        </>
      ) : (
        <>
          <div className="alert alert-success mb-4">File uploaded successfully</div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-4">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">File</p>
                <p className="font-medium text-gray-900 text-sm truncate">{fileInfo.name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Rows</p>
                <p className="font-semibold text-gray-900 text-lg">{fileInfo.rows.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Columns</p>
                <p className="font-semibold text-gray-900 text-lg">{fileInfo.columns}</p>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Columns</p>
              <div className="flex flex-wrap gap-2">
                {fileInfo.columnNames.map((col, idx) => (
                  <span key={idx} className="px-2 py-1 bg-white border border-gray-200 rounded text-sm text-gray-700">
                    {col}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={handleRemoveFile} className="btn btn-secondary">
              <Upload className="w-4 h-4" /> Change File
            </button>
            <button onClick={handleSaveDataset} disabled={savingDataset} className="btn btn-success">
              {savingDataset ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : <><Save className="w-4 h-4" /> Save</>}
            </button>
            <div className="flex-1" />
            <button disabled className="btn btn-primary opacity-50 cursor-not-allowed">
              <Check className="w-4 h-4" /> Ready
            </button>
          </div>
        </>
      )}

      {errorMessage && (
        <div className="alert alert-error mt-4">
          <span className="font-medium">Error:</span> {errorMessage}
        </div>
      )}
    </div>
  );
};
